import * as React from "react";
import { cn } from "../../lib/cn";

export interface SurfaceEmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Lucide icon or any node — rendered in a glassmorph chip. */
  icon?: React.ReactNode;
  /** Headline (e.g. "Nessun documento ancora"). */
  title: React.ReactNode;
  /** Sub-copy — sentence-case helper text. */
  description?: React.ReactNode;
  /** Primary CTA button — caller controls click handler + label. */
  action?: React.ReactNode;
}

/**
 * SurfaceEmptyState — uniform empty-state pattern (v18 mockup vibe:
 * glassmorph card, soft icon chip, italian helper copy + CTA).
 *
 * Used by every surface when:
 *   - the filtered list resolves to zero items, or
 *   - the user has no connectors / data wired yet for the surface.
 */
export const SurfaceEmptyState = React.forwardRef<
  HTMLDivElement,
  SurfaceEmptyStateProps
>(({ icon, title, description, action, className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col items-center justify-center gap-3 rounded-lg border border-hairline border-dashed bg-card/40 px-8 py-12 text-center",
      className,
    )}
    {...rest}
  >
    {icon ? (
      <span
        className="flex h-10 w-10 items-center justify-center rounded-md bg-ando-orange-soft text-accent"
        aria-hidden
      >
        {icon}
      </span>
    ) : null}
    <div className="font-medium text-[14px] text-ink-1">{title}</div>
    {description ? (
      <div className="max-w-sm text-[12.5px] text-ink-2 leading-relaxed">
        {description}
      </div>
    ) : null}
    {action ? <div className="mt-2">{action}</div> : null}
  </div>
));
SurfaceEmptyState.displayName = "SurfaceEmptyState";
